import { type V3 } from "backoffice-api"
import { formatDate, getDateTimeFormatters } from "format"
import type { TFunction } from "i18next"
import { formatTimeUnit } from "utils"
import { getTimeDifference } from "./getTimeDifference"

type EventDate = V3["getProductCourse"]["data"]

export const formatEventDate = (eventDate: EventDate, locale: string) => {
  const { formatTime } = getDateTimeFormatters()

  const month = formatDate(eventDate.start_datetime, locale, { month: "short" })
  const day = formatDate(eventDate.start_datetime, locale, { day: "2-digit" })
  const time = formatTime(eventDate.start_datetime)

  return { month, day, time }
}

export const formatEventDayMonth = (eventDate: EventDate, locale: string) => {
  const startDateTime = new Date(eventDate.start_datetime)
  const endDateTime = new Date(eventDate.end_datetime)

  const startDate = formatDate(startDateTime, locale, {
    day: "numeric",
    month: "short",
  })
  const endDate = formatDate(endDateTime, locale, {
    day: "numeric",
    month: "short",
  })

  if (startDateTime.getDate() !== endDateTime.getDate())
    return `${startDate} - ${endDate}`
  return startDate
}

export const formatEventDay = (eventDate: EventDate) => {
  const startDay = new Date(eventDate.start_datetime).getDate()
  const endDay = new Date(eventDate.end_datetime).getDate()

  if (startDay !== endDay) return `${startDay}-${endDay}`
  return startDay.toString()
}

export const formatEventLength = (
  eventDate: EventDate,
  t: TFunction,
  locale: string
) => {
  const startDate = new Date(eventDate.start_datetime)
  const endDate = new Date(eventDate.end_datetime)
  const { days, hours, minutes } = getTimeDifference(startDate, endDate)

  if (days > 0) return formatTimeUnit(days + 1, "date.DAY_SHORT", t, locale)

  if (hours > 0) {
    const remainingMinutes = minutes - hours * 60

    return minutes > 0
      ? `${formatTimeUnit(
          hours,
          "date.HOUR_SHORT",
          t,
          locale
        )} ${formatTimeUnit(remainingMinutes, "date.MINUTE_SHORT", t, locale)}`
      : formatTimeUnit(hours, "date.HOUR_SHORT", t, locale)
  }

  return formatTimeUnit(minutes, "date.MINUTE_SHORT", t, locale)
}

export const pickEventDate = (dates: EventDate[]) => {
  const validDates = dates.filter((date) => !date.cancelled)
  const firstAttendingDate = validDates.find(
    (date) => date.is_attending || date.is_on_waiting_list
  )
  const firstDate = validDates[0]
  return firstAttendingDate ?? firstDate
}

export const getHasEventExpired = (event: EventDate) => {
  const eventDate = new Date(event.end_datetime).getTime()
  return eventDate <= Date.now()
}

export const getIsEventFull = (event: EventDate | undefined) => {
  if (event === undefined) return false

  return event.attendees_count >= event.attendees_count_max
}

export const getIsEventPastDeadline = (deadline: string | null) => {
  if (deadline === null) return false

  const deadlineTimestamp = new Date(deadline).getTime()

  return deadlineTimestamp <= Date.now()
}
